import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Tent, User, LogIn, Download } from 'lucide-react';
import { CampAccommodationTable as TableType } from '../../types/camp';
import { useCampAccommodationStore } from '../../stores/campAccommodationStore';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { useConfirm } from '../../hooks/useConfirm';
import toast from 'react-hot-toast';
import { exportCampAccommodationToExcel } from '../../utils/exportCampAccommodation';
import { useLanguage } from '../../contexts/LanguageContext';

type Props = {
  table: TableType;
};

export const CampAccommodationTable = ({ table }: Props) => {
  const { translations } = useLanguage();
  const navigate = useNavigate();
  const { deleteTable } = useCampAccommodationStore();
  const { isOpen, config, confirm, close } = useConfirm();
  const userRole = localStorage.getItem('userRole');
  const isEmployee = userRole === 'admin';

  const handleExport = () => {
    try {
      exportCampAccommodationToExcel(table);
      toast.success(translations?.campAccommodationTable?.exportSuccess || 'Accommodation data exported successfully');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error(translations?.campAccommodationTable?.exportError || 'Failed to export accommodation data');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteTable(table.id);
      toast.success(translations?.campAccommodationTable?.deleteSuccess || 'Accommodation table deleted successfully');
    } catch (error) {
      toast.error(translations?.campAccommodationTable?.deleteError || 'Failed to delete accommodation table');
    }
  };

  const confirmDelete = () => {
    confirm({
      title: translations?.campAccommodationTable?.deleteTitle || 'Delete Accommodation Table',
      message: `${translations?.campAccommodationTable?.deleteMessage || 'Are you sure you want to delete the table'} "${table.tableName}"?`,
      onConfirm: handleDelete,
      type: 'danger',
    });
  };

  // Calculate statistics
  const totalTents = table.tents.length;
  const occupiedTents = table.tents.filter((tent) => tent.occupiedCapacity > 0).length;
  const totalCapacity = table.totalCapacity;
  const occupiedCapacity = table.occupiedCapacity;
  const occupancyRate = Math.round((occupiedCapacity / totalCapacity) * 100);

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="p-6 border-b">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-primary-50 rounded-xl">
              <Tent className="text-primary-500" size={24} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {table.tableName}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <Tent size={16} className="text-gray-400" />
                <p className="text-sm text-gray-500">{table.campName}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {/* Tents Stats */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <Tent size={18} className="text-primary-500" />
              <span className="text-sm text-gray-600">
                {translations?.campAccommodationTable?.tents || 'Tents'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">
                {translations?.campAccommodationTable?.occupiedTotal || 'Occupied / Total'}
              </span>
              <span className="text-lg font-semibold">
                {occupiedTents} / {totalTents}
              </span>
            </div>
            <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
              <div
                className="h-2 rounded-full bg-primary-500"
                style={{ width: `${(occupiedTents / totalTents) * 100}%` }}
              />
            </div>
          </div>

          {/* Capacity Stats */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <User size={18} className="text-primary-500" />
              <span className="text-sm text-gray-600">
                {translations?.campAccommodationTable?.capacity || 'Capacity'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">
                {translations?.campAccommodationTable?.occupiedTotal || 'Occupied / Total'}
              </span>
              <span className="text-lg font-semibold">
                {occupiedCapacity} / {totalCapacity}
              </span>
            </div>
            <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${
                  occupancyRate > 90
                    ? 'bg-red-500'
                    : occupancyRate > 70
                    ? 'bg-yellow-500'
                    : 'bg-green-500'
                }`}
                style={{ width: `${occupancyRate}%` }}
              />
            </div>
          </div>

          {/* Occupancy Rate */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <User size={18} className="text-primary-500" />
              <span className="text-sm text-gray-600">
                {translations?.campAccommodationTable?.occupancyRate || 'Occupancy Rate'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">
                {translations?.campAccommodationTable?.percentage || 'Percentage'}
              </span>
              <span
                className={`text-lg font-semibold ${
                  occupancyRate > 90
                    ? 'text-red-500'
                    : occupancyRate > 70
                    ? 'text-yellow-500'
                    : 'text-green-500'
                }`}
              >
                {occupancyRate}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="px-6 py-4 bg-gray-50 flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
          <button
            onClick={() => navigate(`/camp-accommodation/${table.id}`)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors w-full sm:w-auto justify-center"
          >
            <LogIn size={18} />
            <span>{translations?.campAccommodationTable?.viewDetails || 'View Details'}</span>
          </button>

          {isEmployee && (
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors w-full sm:w-auto justify-center"
              title={translations?.campAccommodationTable?.exportTitle || 'Download Accommodation'}
            >
              <Download size={18} />
              <span>{translations?.campAccommodationTable?.exportButton || 'Download'}</span>
            </button>
          )}
        </div>

        {isEmployee && (
          <button
            onClick={confirmDelete}
            className="text-red-500 hover:text-red-600 text-sm w-full sm:w-auto text-center"
          >
            {translations?.campAccommodationTable?.deleteButton || 'Delete Table'}
          </button>
        )}
      </div>

      {/* Confirm Dialog */}
      {isOpen && config && (
        <ConfirmDialog
          isOpen={isOpen}
          onClose={close}
          onConfirm={config.onConfirm}
          title={config.title}
          message={config.message}
          type={config.type}
          confirmText={translations?.campAccommodationTable?.confirmDelete || 'Delete'}
          cancelText={translations?.campAccommodationTable?.cancelDelete || 'Cancel'}
        />
      )}
    </div>
  );
};